import type {
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryReturnValue,
} from '@reduxjs/toolkit/query';
import { groupBy } from 'es-toolkit';

import { apiConfig } from '../../config/config.bundle';
import type {
  ApiResponse,
  CartItem,
  ItemType,
  ListItem,
  MenuItem,
  PackageItem,
  RecentSearch,
  SavedForLaterItem,
  TransformedCartItem,
} from '../../types/api-types';
import { enhanceApi } from '../../utils/api-utils';
import { getScopedApi } from './base-api';

const productApiConfig = apiConfig.product;

type OrderableMenuQueryArg = {
  accountNumber: string;
  agreementNumber: string;
};

type RecentSearchesQueryArg = {
  userId: string;
};

type SaveRecentSearchMutationArg = {
  body: Omit<RecentSearch, 'createdAt' | 'orderBy'>;
};

type ClearRecentSearchesMutationArg = {
  searchId?: string | null;
  userId: string;
};

type CartQueryArg = {
  accountNumber: string;
  // userId: string;
};

type AddToCartItem = {
  accountNumber: string;
  userId: string;
  userFullName: string;
  item: string;
  itemDescription: string;
  itemType?: ItemType;
  agreementNumber?: string | number;
  quantity: number | string;
  imagePath: string;
  parentItem?: string;
  originalItem?: string;
  orderNumber?: string | number;
  packageName?: string;
  poNumber?: string;
};

type AddToCartMutationArg = {
  body: AddToCartItem;
};

type UpdateCartItemMutationArg = {
  params: { accountNumber: string; sk: string };
} & AddToCartMutationArg;

type UpsertCartItemsMutationArg = {
  body: {
    items: (AddToCartItem & { sk?: string })[];
  };
};

type DeleteCartItemMutationArg = {
  accountNumber: string;
  item: string;
};

type DeleteCartItemsMutationArg = {
  accountNumber: string;
  items?: string[];
  sks?: string[];
};

type ClearCartMutationArg = {
  accountNumber: string;
};

type FavoritesQueryArg = {
  userId: string;
};

type ListsQueryArg = {
  accountNumbers: string[];
};

type AddToListItem = {
  accountNumber: string;
  item: string | null;
  itemType?: 'PACKAGE' | (string & {});
  quantity: number | string;
  imagePath: string;
  listName: string;
  userId: string;
  userFullName: string;
};

type UpsertListItemsMutationArg = {
  body: {
    items: (AddToListItem & { sk?: string })[];
  };
};

type DeleteListItemMutationArg = {
  accountNumber: string;
  sk: string;
};
type ProductCategoryQueryArg = {
  sku: string;
};

export type ProductCategory = {
  sku: string;
  categoryId: string;
};

type UpdateListMutationArg = {
  params: {
    accountNumber: string;
    oldListName: string;
    newListName: string;
  };
};

type DeleteListMutationArg = {
  accountNumber: string;
  listName: string;
};

type SavedForLaterQueryArg = {
  accountNumber: string;
};

type SaveForLaterItem = {
  accountNumber: string;
  userId: string;
  userFullName: string;
  item: string;
  quantity: number | string;
  imagePath: string;
};

type UpsertSavedForLaterItemsMutationArg = {
  body: {
    items: (SaveForLaterItem & { sk?: string })[];
  };
};

type DeleteSavedForLaterItemMutationArg = {
  accountNumber: string;
  sk: string;
};

type PackagesQueryArg = {
  accountNumbers: string[];
};

type AddToPackageItem = {
  packageReference?: string;
  accountNumber: string;
  item: string | null;
  itemDescription: string;
  itemType?: string;
  quantity: number | string;
  imagePath: string;
  parentItem?: string;
  packageName: string;
  userId: string;
  userFullName: string;
};

type UpsertPackageItemsMutationArg = {
  body: {
    items: (AddToPackageItem & { sk?: string })[];
  };
};

type DeletePackageMutationArg = {
  accountNumber: string;
  packageName: string;
};

type DeletePackageItemMutationArg = {
  accountNumber: string;
  sk: string;
};

type UpdatePackageMutationArg = {
  params: {
    accountNumber: string;
    oldPackageName: string;
    newPackageName: string;
  };
};

type DeletePackageItemsMutationArg = {
  accountNumber: string;
  sks: string[];
};

const productApi = getScopedApi('product').injectEndpoints({
  endpoints: builder => ({
    menu: builder.query<MenuItem[], void>({
      query: () => ({
        method: 'GET',
        url: productApiConfig.paths.products,
        params: { requestType: 'MENU_ITEM_GET_ALL' },
      }),
      transformResponse: (response: ApiResponse<MenuItem[]>) =>
        response.responseObject.toSorted((a, b) => a.order - b.order),
    }),
    orderableMenu: builder.query<MenuItem[], OrderableMenuQueryArg>({
      query: arg => ({
        method: 'GET',
        url: productApiConfig.paths.products,
        params: { requestType: 'MENU_ITEM_GET_ALL_WITH_PRICING', ...arg },
      }),
      transformResponse: (response: ApiResponse<MenuItem[]>) => {
        const filteredMenu = structuredClone(response.responseObject);

        const getFilteredChildren = (menuItem: MenuItem): MenuItem[] => {
          return (
            menuItem.children
              ?.filter(childMenuItem => childMenuItem.active)
              .map(childMenuItem => ({
                ...childMenuItem,
                children: childMenuItem.children
                  ? getFilteredChildren(childMenuItem)
                  : childMenuItem.children,
              }))
              .filter(
                childMenuItem =>
                  !childMenuItem.children || childMenuItem.children.length
              ) || []
          );
        };

        filteredMenu.forEach(menuItem => {
          menuItem.children = menuItem.children
            ? getFilteredChildren(menuItem)
            : menuItem.children;
        });

        return filteredMenu.toSorted((a, b) => a.order - b.order);
      },
    }),
    recentSearches: builder.query<
      ApiResponse<RecentSearch[]>,
      RecentSearchesQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: productApiConfig.paths.products,
        params: { requestType: 'RECENT_SEARCH_GET_BY_PK', ...arg },
      }),
      providesTags: ['RecentSearches' as const],
    }),
    saveSearch: builder.mutation<
      ApiResponse<RecentSearch[]>,
      SaveRecentSearchMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: productApiConfig.paths.products,
        params: { requestType: 'RECENT_SEARCH_INSERT' },
        body: arg.body,
      }),
      invalidatesTags: ['RecentSearches'],
    }),
    clearRecentSearches: builder.mutation<
      ApiResponse<RecentSearch[]>,
      ClearRecentSearchesMutationArg
    >({
      query: arg => ({
        method: 'DELETE',
        url: productApiConfig.paths.products,
        params: {
          requestType: arg.searchId
            ? 'RECENT_SEARCH_DELETE'
            : 'RECENT_SEARCH_DELETE_BY_USER',
          ...arg,
        },
      }),
      invalidatesTags: ['RecentSearches'],
    }),

    cart: builder.query<ApiResponse<TransformedCartItem[]>, CartQueryArg>({
      query: arg => ({
        method: 'GET',
        url: productApiConfig.paths.products,
        params: { requestType: 'CART_DETAILS_GET_BY_PK', ...arg },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'Cart', id: arg.accountNumber },
      ],
      transformResponse: (response: ApiResponse<CartItem[]>) => {
        if (response.responseObject) {
          const groupedCartItems = groupBy(
            response.responseObject,
            item =>
              `${item.item}${item.parentItem ? `|${item.parentItem}` : ''}${item.packageName ? `|${item.packageName}` : ''}`
          );

          const responseObject: TransformedCartItem[] = [];

          for (const key in groupedCartItems) {
            responseObject.push(
              groupedCartItems[key].reduce(
                (acc, cur) => {
                  if (!acc) {
                    return {
                      ...cur,
                      originalItems: [
                        {
                          ...cur,
                        },
                      ],
                    };
                  }
                  return {
                    ...acc,
                    ...cur,
                    quantity: acc.quantity + cur.quantity,
                    originalItems: [
                      ...acc.originalItems,
                      {
                        ...cur,
                      },
                    ],
                  };
                },
                null as TransformedCartItem | null
              )!
            );
          }
          return {
            ...response,
            responseObject,
          };
        }
        return { ...response, responseObject: [] };
      },
    }),
    addToCart: builder.mutation<ApiResponse<CartItem>, AddToCartMutationArg>({
      query: arg => ({
        method: 'POST',
        url: productApiConfig.paths.products,
        body: arg.body,
        params: {
          requestType: 'CART_DETAILS_INSERT',
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Cart',
          id: arg.body.accountNumber,
        },
      ],
    }),
    updateCartItem: builder.mutation<
      ApiResponse<CartItem[]>,
      UpdateCartItemMutationArg
    >({
      query: arg => ({
        method: 'PUT',
        url: productApiConfig.paths.products,
        body: arg.body,
        params: {
          requestType: 'CART_DETAILS_UPDATE',
          ...arg.params,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Cart',
          id: arg.params.accountNumber,
        },
      ],
    }),
    upsertCartItems: builder.mutation<
      ApiResponse<CartItem[]>,
      UpsertCartItemsMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: productApiConfig.paths.products,
        body: arg.body,
        params: {
          requestType: 'CART_DETAILS_BULK_INSERT',
        },
        meta: {
          batch: {
            property: 'items',
            in: 'body',
            size: 50,
            merge: (
              results: QueryReturnValue<
                ApiResponse<CartItem[]>,
                FetchBaseQueryError,
                FetchBaseQueryMeta
              >[]
            ) => {
              const allData = results
                .map(result => result.data)
                .filter(data => !!data);

              return allData.reduce((acc, cur) => {
                return {
                  ...cur,
                  responseObject: [
                    ...acc.responseObject,
                    ...cur.responseObject,
                  ],
                };
              });
            },
          },
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Cart',
          id: arg.body.items[0].accountNumber,
        },
      ],
    }),
    deleteCartItem: builder.mutation<
      ApiResponse<unknown>,
      DeleteCartItemMutationArg
    >({
      query: arg => ({
        method: 'DELETE',
        url: productApiConfig.paths.products,
        params: {
          requestType: 'CART_DETAILS_DELETE_BY_ACCOUNT_ITEM',
          ...arg,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Cart',
          id: arg.accountNumber,
        },
      ],
    }),
    deleteCartItems: builder.mutation<
      ApiResponse<unknown>,
      DeleteCartItemsMutationArg
    >({
      query: arg => {
        const { sks: skus, items, ...rest } = arg;

        return {
          method: 'DELETE',
          url: productApiConfig.paths.products,
          params: {
            requestType: items
              ? 'CART_DETAILS_DELETE_BY_ACCOUNT_ITEMS'
              : 'CART_DETAILS_DELETE_BY_ACCOUNT_SKUS',
            ...rest,
            items: items?.join(','),
            skus: skus?.join(','),
          },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Cart',
          id: arg.accountNumber,
        },
      ],
    }),
    clearCart: builder.mutation<ApiResponse<unknown>, ClearCartMutationArg>({
      query: arg => ({
        method: 'DELETE',
        url: productApiConfig.paths.products,
        params: {
          requestType: 'CART_DETAILS_DELETE_BY_PK',
          ...arg,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Cart',
          id: arg.accountNumber,
        },
      ],
    }),
    favorites: builder.query<
      Record<
        string,
        {
          id: string;
          accountNumber: string;
          name: string;
          items: (Omit<ListItem, 'item'> & { item: string })[];
        }
      >,
      FavoritesQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: productApiConfig.paths.products,
        params: {
          requestType: 'FORBUSINESS_LIST_GET_BY_USER_ID',
          ...arg,
        },
      }),
      transformResponse: (response: ApiResponse<ListItem[]>, _, arg) => {
        return (response.responseObject || [])
          .filter(
            listItem =>
              listItem.accountNumber === `favorites#${arg.userId.toLowerCase()}`
          )
          .reduce(
            (acc, cur) => {
              const key = `${cur.listName}`;

              if (acc[key]) {
                if (cur.item) {
                  acc[key].items.push(
                    cur as Omit<ListItem, 'item'> & { item: string }
                  );
                }
              } else {
                acc[key] = {
                  id: key,
                  name: cur.listName,
                  accountNumber: cur.accountNumber,
                  items: cur.item
                    ? [cur as Omit<ListItem, 'item'> & { item: string }]
                    : [],
                };
              }

              return acc;
            },
            {} as Record<
              string,
              {
                id: string;
                accountNumber: string;
                name: string;
                items: (Omit<ListItem, 'item'> & { item: string })[];
              }
            >
          );
      },
      providesTags: (_result, _error, arg) => [
        {
          type: 'Favorites',
          id: arg.userId.toLowerCase(),
        },
      ],
    }),
    upsertFavoriteItems: builder.mutation<
      ApiResponse<ListItem[]>,
      UpsertListItemsMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: productApiConfig.paths.products,
        body: arg.body,
        params: {
          requestType: 'FORBUSINESS_LIST_BULK_INSERT',
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Favorites',
          id: arg.body.items[0].userId.toLowerCase(),
        },
      ],
    }),
    deleteFavoriteItem: builder.mutation<
      ApiResponse<unknown>,
      DeleteListItemMutationArg & { userId: string }
    >({
      query: arg => {
        return {
          method: 'DELETE',
          url: productApiConfig.paths.products,
          params: {
            requestType: 'FORBUSINESS_LIST_DELETE',
            ...arg,
          },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Favorites',
          id: arg.userId.toLowerCase(),
        },
      ],
    }),
    lists: builder.query<
      Record<
        string,
        {
          id: string;
          accountNumber: string;
          name: string;
          items: (Omit<ListItem, 'item'> & { item: string })[];
        }
      >,
      ListsQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: productApiConfig.paths.products,
        params: {
          requestType: 'FORBUSINESS_LIST_GET_BY_ACCOUNT_NUMBERS',
          accountNumbers: arg.accountNumbers.toSorted((a, b) => +a - +b),
        },
      }),
      transformResponse: (response: ApiResponse<ListItem[]>) => {
        return (response.responseObject || []).reduce(
          (acc, cur) => {
            const key = `${cur.accountNumber}#${cur.listName}`;

            if (acc[key]) {
              if (cur.item) {
                acc[key].items.push(
                  cur as Omit<ListItem, 'item'> & { item: string }
                );
              }
            } else {
              acc[key] = {
                id: key,
                name: cur.listName,
                accountNumber: cur.accountNumber,
                items: cur.item
                  ? [cur as Omit<ListItem, 'item'> & { item: string }]
                  : [],
              };
            }

            return acc;
          },
          {} as Record<
            string,
            {
              id: string;
              accountNumber: string;
              name: string;
              items: (Omit<ListItem, 'item'> & { item: string })[];
            }
          >
        );
      },
      providesTags: (_result, _error, arg) =>
        arg.accountNumbers.map(accountNumber => ({
          type: 'Lists',
          id: accountNumber,
        })),
    }),
    upsertListItems: builder.mutation<
      ApiResponse<ListItem[]>,
      UpsertListItemsMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: productApiConfig.paths.products,
        body: arg.body,
        params: {
          requestType: 'FORBUSINESS_LIST_BULK_INSERT',
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Lists',
          id: arg.body.items[0].accountNumber,
        },
      ],
    }),
    deleteListItem: builder.mutation<
      ApiResponse<unknown>,
      DeleteListItemMutationArg
    >({
      query: arg => {
        return {
          method: 'DELETE',
          url: productApiConfig.paths.products,
          params: {
            requestType: 'FORBUSINESS_LIST_DELETE',
            ...arg,
          },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Lists',
          id: arg.accountNumber,
        },
      ],
    }),
    updateList: builder.mutation<ApiResponse<unknown>, UpdateListMutationArg>({
      query: arg => {
        return {
          method: 'PUT',
          url: productApiConfig.paths.products,
          params: {
            requestType: 'FORBUSINESS_LIST_UPDATE_LIST_NAME_BY_ACCOUNT',
            ...arg.params,
          },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Lists',
          id: arg.params.accountNumber,
        },
      ],
    }),
    deleteList: builder.mutation<ApiResponse<unknown>, DeleteListMutationArg>({
      query: arg => {
        return {
          method: 'DELETE',
          url: productApiConfig.paths.products,
          params: {
            requestType: 'FORBUSINESS_LIST_DELETE_BY_ACCOUNT_LIST_NAME',
            ...arg,
          },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Lists',
          id: arg.accountNumber,
        },
      ],
    }),
    savedForLater: builder.query<
      ApiResponse<SavedForLaterItem[]>,
      SavedForLaterQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: productApiConfig.paths.products,
        params: {
          requestType: 'SAVE_FOR_LATER_GET_BY_PK',
          ...arg,
        },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'SavedForLater', id: arg.accountNumber },
      ],
    }),
    upsertSavedForLaterItems: builder.mutation<
      ApiResponse<SavedForLaterItem[]>,
      UpsertSavedForLaterItemsMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: productApiConfig.paths.products,
        body: arg.body,
        params: {
          requestType: 'SAVE_FOR_LATER_BULK_INSERT',
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'SavedForLater',
          id: arg.body.items[0].accountNumber,
        },
      ],
    }),
    deleteSavedForLaterItem: builder.mutation<
      ApiResponse<unknown>,
      DeleteSavedForLaterItemMutationArg
    >({
      query: arg => {
        return {
          method: 'DELETE',
          url: productApiConfig.paths.products,
          params: {
            requestType: 'SAVE_FOR_LATER_DELETE',
            ...arg,
          },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'SavedForLater',
          id: arg.accountNumber,
        },
      ],
    }),
    packages: builder.query<
      Record<
        string,
        {
          id: string;
          accountNumber: string;
          name: string;
          items: PackageItem[];
        }
      >,
      PackagesQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: productApiConfig.paths.products,
        params: {
          requestType: 'FORBUSINESS_PACKAGE_GET_BY_ACCOUNT_NUMBERS',
          accountNumbers: arg.accountNumbers.toSorted((a, b) => +a - +b),
        },
      }),
      transformResponse: (response: ApiResponse<PackageItem[]>) => {
        return (response.responseObject || []).reduce(
          (acc, cur) => {
            const key = cur.packageReference;

            if (acc[key]) {
              if (cur.item) {
                acc[key].items.push(
                  cur as Omit<PackageItem, 'item'> & { item: string }
                );
              }
            } else {
              acc[key] = {
                id: cur.packageReference,
                name: cur.packageName,
                accountNumber: cur.accountNumber,
                items: cur.item
                  ? [cur as Omit<PackageItem, 'item'> & { item: string }]
                  : [],
              };
            }

            return acc;
          },
          {} as Record<
            string,
            {
              id: string;
              accountNumber: string;
              name: string;
              items: (Omit<PackageItem, 'item'> & { item: string })[];
            }
          >
        );
      },
      providesTags: (_result, _error, arg) =>
        arg.accountNumbers.map(accountNumber => ({
          type: 'Packages',
          id: accountNumber,
        })),
    }),
    upsertPackageItems: builder.mutation<
      ApiResponse<PackageItem[]>,
      UpsertPackageItemsMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: productApiConfig.paths.products,
        body: arg.body,
        params: {
          requestType: 'FORBUSINESS_PACKAGE_BULK_INSERT',
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Packages',
          id: arg.body.items[0].accountNumber,
        },
      ],
    }),
    deletePackageItem: builder.mutation<
      ApiResponse<unknown>,
      DeletePackageItemMutationArg
    >({
      query: arg => {
        return {
          method: 'DELETE',
          url: productApiConfig.paths.products,
          params: {
            requestType: 'FORBUSINESS_PACKAGE_DELETE',
            ...arg,
          },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Packages',
          id: arg.accountNumber,
        },
      ],
    }),
    deletePackageItems: builder.mutation<
      ApiResponse<unknown>,
      DeletePackageItemsMutationArg
    >({
      query: arg => ({
        method: 'DELETE',
        url: productApiConfig.paths.products,
        params: {
          requestType: 'FORBUSINESS_PACKAGE_DELETE_BY_ACCOUNT_SKS',
          ...arg,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Packages',
          id: arg.accountNumber,
        },
      ],
    }),
    updatePackage: builder.mutation<
      ApiResponse<unknown>,
      UpdatePackageMutationArg
    >({
      query: arg => {
        return {
          method: 'PUT',
          url: productApiConfig.paths.products,
          params: {
            requestType: 'FORBUSINESS_PACKAGE_UPDATE_PACKAGE_NAME_BY_ACCOUNT',
            ...arg.params,
          },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Packages',
          id: arg.params.accountNumber,
        },
      ],
    }),
    deletePackage: builder.mutation<
      ApiResponse<unknown>,
      DeletePackageMutationArg
    >({
      query: arg => {
        return {
          method: 'DELETE',
          url: productApiConfig.paths.products,
          params: {
            requestType: 'FORBUSINESS_PACKAGE_DELETE_BY_ACCOUNT_PACKAGE_NAME',
            ...arg,
          },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'Packages',
          id: arg.accountNumber,
        },
      ],
    }),
    productCategory: builder.query<
      ApiResponse<ProductCategory[]>,
      ProductCategoryQueryArg
    >({
      query: ({ sku }) => ({
        method: 'GET',
        url: productApiConfig.paths.products,
        params: {
          requestType: 'PRODUCT_DETAILS_GET_BY_SKU',
          skus: sku,
        },
      }),
    }),
  }),
});

export const {
  useMenuQuery,
  useLazyMenuQuery,
  useOrderableMenuQuery,
  useLazyOrderableMenuQuery,
  useRecentSearchesQuery,
  useLazyRecentSearchesQuery,
  useSaveSearchMutation,
  useClearRecentSearchesMutation,
  useCartQuery,
  useLazyCartQuery,
  useAddToCartMutation,
  useUpsertCartItemsMutation,
  useUpdateCartItemMutation,
  useDeleteCartItemMutation,
  useDeleteCartItemsMutation,
  useClearCartMutation,
  useFavoritesQuery,
  useLazyFavoritesQuery,
  useUpsertFavoriteItemsMutation,
  useDeleteFavoriteItemMutation,
  useListsQuery,
  useLazyListsQuery,
  useUpsertListItemsMutation,
  useDeleteListItemMutation,
  useUpdateListMutation,
  useDeleteListMutation,
  useSavedForLaterQuery,
  useLazySavedForLaterQuery,
  useUpsertSavedForLaterItemsMutation,
  useDeleteSavedForLaterItemMutation,
  usePackagesQuery,
  useLazyPackagesQuery,
  useUpsertPackageItemsMutation,
  useUpdatePackageMutation,
  useDeletePackageItemMutation,
  useDeletePackageItemsMutation,
  useDeletePackageMutation,
  useProductCategoryQuery,
  useLazyProductCategoryQuery,
} = enhanceApi(productApi);

export default productApi;
